import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { Save, ExternalLink, Edit3, X, RotateCcw, Copy, ChefHat, Zap, Building2,
  Eye, Sparkles, ArrowRight, Calculator, FileText, MessageCircle, AlertCircle,
} from "lucide-react";
import CatalogoSubnav from "../../components/CatalogoSubnav";
import PageHeader from "../../components/PageHeader";
import { useAuth } from "../../App";

const DEFAULTS = {
  minimo_personas: 30,
  anticipo_default: 5000,
  iva_activo: false,
  iva_porcentaje: 16,
  duracion_base_horas: 5,
  dias_liquidacion: 5,
  vigencia_dias: 15,
  tipo_cotizacion_default: "instalaciones_mas_menu",
  condiciones_comerciales: "Esta cotizacion es informativa y esta sujeta a disponibilidad de fecha. Para apartar el evento se requiere anticipo.",
  politicas_pago: "Anticipo para apartar fecha. Saldo a liquidar 5 dias antes del evento.",
  mensaje_whatsapp_template: "",
};

const CotizadorConfig = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [original, setOriginal] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [identidades, setIdentidades] = useState([]);
  const [idnForm, setIdnForm] = useState(null); // { identidad_id?, nombre_comercial, ... }

  const reloadIdentidades = () =>
    axios.get(`${API}/identidades-comerciales`).then(r => setIdentidades((r.data || []).filter(x => x.activo !== false)));

  const saveIdentidad = async () => {
    if (!idnForm.nombre_comercial?.trim()) { toast.error("Nombre comercial requerido"); return; }
    try {
      if (idnForm.identidad_id) {
        await axios.put(`${API}/identidades-comerciales/${idnForm.identidad_id}`, idnForm);
        toast.success("Identidad actualizada");
      } else {
        await axios.post(`${API}/identidades-comerciales`, idnForm);
        toast.success("Identidad creada");
      }
      setIdnForm(null);
      reloadIdentidades();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar identidad"); }
  };

  const desactivarIdentidad = async (id) => {
    if (!window.confirm("¿Desactivar esta identidad? No podrá seleccionarse en cotizaciones nuevas.")) return;
    try { await axios.delete(`${API}/identidades-comerciales/${id}`); toast.success("Identidad desactivada"); reloadIdentidades(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/cotizador-config`).catch(() => ({ data: null })),
      axios.get(`${API}/identidades-comerciales`).catch(() => ({ data: [] })),
    ]).then(([cfgRes, idnRes]) => {
      if (cfgRes.data) { setCfg(cfgRes.data); setOriginal(cfgRes.data); }
      else toast.error("Error al cargar configuracion");
      setIdentidades((idnRes.data || []).filter(x => x.activo !== false));
    });
  }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const { config_id: _ignore1, _id: _ignore2, ...payload } = cfg;
      void _ignore1; void _ignore2;
      await axios.put(`${API}/admin/cotizador-config`, payload);
      toast.success("Configuracion guardada correctamente");
      setOriginal(cfg);
      setEditMode(false);
    } catch {
      toast.error("Error al guardar. Reintenta.");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setCfg(original);
    setEditMode(false);
    toast.info("Cambios cancelados");
  };

  const restore = async () => {
    setRestoreOpen(false);
    const restored = { ...cfg, ...DEFAULTS };
    setCfg(restored);
    setSaving(true);
    try {
      await axios.put(`${API}/admin/cotizador-config`, restored);
      toast.success("Configuracion restaurada a valores iniciales");
      setOriginal(restored);
      setEditMode(false);
    } catch {
      toast.error("Error al restaurar");
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setCfg(p => ({ ...p, [k]: v }));

  // Iter141 · Tipos de cotización personalizados por tenant.
  // Se guardan en cfg.tipos_cotizacion_custom = [{ key, label }] y se envían
  // como parte del payload PUT /admin/cotizador-config (el backend hace $set,
  // soporta campos extra sin cambios).
  const tiposCustom = Array.isArray(cfg?.tipos_cotizacion_custom) ? cfg.tipos_cotizacion_custom : [];
  const [nuevoTipoOpen, setNuevoTipoOpen] = useState(false);
  const [nuevoTipoNombre, setNuevoTipoNombre] = useState("");
  const slugifyTipo = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || `tipo_${Date.now().toString(36)}`;
  const agregarTipoCustom = () => {
    const label = nuevoTipoNombre.trim();
    if (!label) { toast.error("Escribe un nombre para el tipo"); return; }
    let key = slugifyTipo(label);
    const existentes = new Set(["instalaciones_mas_menu", "paquete_completo", ...tiposCustom.map(t => t.key)]);
    if (existentes.has(key)) { key = `${key}_${Date.now().toString(36).slice(-4)}`; }
    const nuevos = [...tiposCustom, { key, label }];
    setCfg(p => ({ ...p, tipos_cotizacion_custom: nuevos, tipo_cotizacion_default: key }));
    setNuevoTipoNombre("");
    setNuevoTipoOpen(false);
    toast.success(`Tipo "${label}" añadido. Recuerda guardar los cambios.`);
  };
  const eliminarTipoCustom = (key) => {
    const nuevos = tiposCustom.filter(t => t.key !== key);
    const nuevoDefault = cfg.tipo_cotizacion_default === key ? "instalaciones_mas_menu" : cfg.tipo_cotizacion_default;
    setCfg(p => ({ ...p, tipos_cotizacion_custom: nuevos, tipo_cotizacion_default: nuevoDefault }));
  };

  if (!cfg) return <div className="text-center py-12 text-stone-500">Cargando...</div>;

  // Iter124m · La ruta canónica del cotizador público es /cotizar/:usuarioId.
  // Usamos user.user_id (dueño del tenant logueado) para armar la liga.
  const publicUrl = `${window.location.origin}/cotizar/${user?.user_id || ""}`;
  const readonly = !editMode;

  const tipoCotLabel = (() => {
    const key = cfg.tipo_cotizacion_default;
    if (key === "paquete_completo") return "Paquete completo con instalaciones incluidas";
    if (key === "instalaciones_mas_menu") return "Uso de instalaciones + menu a elegir";
    const custom = tiposCustom.find(t => t.key === key);
    return custom ? custom.label : "Uso de instalaciones + menu a elegir";
  })();

  return (
    <div className="space-y-6 pb-20" data-testid="cotizador-config">
      <CatalogoSubnav />
      <PageHeader
        title="Configuracion del Cotizador"
        description="Reglas comerciales que aplican a TODAS las cotizaciones nuevas. Las cotizaciones y contratos anteriores no se modifican."
        primaryAction={!editMode ? {
          label: "Editar configuracion",
          icon: Edit3,
          testId: "btn-editar-config",
          onClick: () => setEditMode(true),
        } : {
          label: saving ? "Guardando..." : "Guardar cambios",
          icon: Save,
          testId: "btn-guardar-config",
          onClick: save,
          disabled: saving,
        }}
        secondaryActions={editMode ? [
          { label: "Cancelar", icon: X, testId: "btn-cancelar-config", onClick: cancel },
          { label: "Restaurar valores iniciales", icon: RotateCcw, testId: "btn-restore-config", onClick: () => setRestoreOpen(true) },
        ] : [
          { label: "Ver cotizador publico", icon: Eye, testId: "btn-ver-publico", onClick: () => window.open(publicUrl, "_blank") },
        ]}
      />

      {readonly && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-3 text-sm text-blue-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Modo lectura. Presiona <strong>Editar configuracion</strong> arriba para modificar los valores.</span>
          </CardContent>
        </Card>
      )}

      {/* Identidad pública predeterminada · Iter124r · P1 */}
      <Card className="border-stone-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-rose-700" />
              <h2 className="font-display text-lg text-stone-900">Identidades comerciales</h2>
            </div>
            <button
              onClick={() => setIdnForm({ nombre_comercial: "", slug: "", logo_url: "", telefono: "", correo: "", direccion: "", color_primario: "#D65F79", color_secundario: "#B84A63", footer_texto: "", activo: true, es_default: identidades.length === 0 })}
              className="px-3 py-1.5 rounded-lg text-xs border bg-white hover:bg-stone-50" data-testid="btn-nueva-identidad">+ Nueva identidad</button>
          </div>
          <p className="text-xs text-stone-600 mb-3">Marcas comerciales del tenant. Cada cotización guarda un snapshot inmutable de la identidad presentada al cliente.</p>

          {identidades.length === 0 ? (
            <div className="text-sm text-stone-500 p-3 rounded border bg-stone-50" data-testid="identidad-config-vacia">
              Aún no has creado identidades. Crea la primera con el botón <b>+ Nueva identidad</b>. Mientras tanto, el cotizador público mostrará el branding general.
            </div>
          ) : (
            <div className="space-y-2 mb-4">
              {identidades.map(i => (
                <div key={i.identidad_id} className="flex items-center gap-3 p-2.5 border rounded-lg bg-white flex-wrap" data-testid={`identidad-row-${i.identidad_id}`}>
                  <div className="w-8 h-8 rounded" style={{background: i.color_primario || '#eee'}} />
                  <div className="flex-1 min-w-[140px]">
                    <div className="text-sm font-medium">{i.nombre_comercial} {i.es_default && <span className="text-[10px] uppercase bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded ml-1">default</span>}</div>
                    <div className="text-xs text-stone-500 truncate">{i.slug} · {i.telefono || 'sin tel'} · {i.correo || 'sin correo'}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setIdnForm({...i})} className="p-1.5 rounded hover:bg-stone-100" title="Editar" data-testid={`btn-edit-identidad-${i.identidad_id}`}><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => desactivarIdentidad(i.identidad_id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Desactivar" data-testid={`btn-del-identidad-${i.identidad_id}`}><X className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-t pt-3">
            <label className="text-xs text-stone-700 font-medium block mb-2">Identidad pública predeterminada del cotizador</label>
            {identidades.length === 0 ? (
              <div className="text-xs text-stone-500">Crea una identidad primero.</div>
            ) : (
              <select
                value={cfg.identidad_publica_default_id || ""}
                onChange={e => setCfg({...cfg, identidad_publica_default_id: e.target.value || null})}
                disabled={readonly}
                className="w-full sm:w-96 px-3 py-2 rounded-lg border text-sm disabled:opacity-70"
                data-testid="select-identidad-publica"
              >
                <option value="">— Usar identidad default del tenant —</option>
                {identidades.map(i => <option key={i.identidad_id} value={i.identidad_id}>{i.nombre_comercial}</option>)}
              </select>
            )}
          </div>
        </CardContent>
      </Card>

      {idnForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={()=>setIdnForm(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="modal-identidad">
            <h3 className="font-display text-lg mb-3">{idnForm.identidad_id ? "Editar" : "Nueva"} identidad comercial</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><label className="text-xs text-stone-600">Nombre comercial *</label><input className="w-full px-3 py-2 rounded-lg border text-sm" value={idnForm.nombre_comercial||""} onChange={e=>setIdnForm({...idnForm, nombre_comercial:e.target.value})} data-testid="idn-input-nombre" /></div>
              <div><label className="text-xs text-stone-600">Slug (opcional)</label><input className="w-full px-3 py-2 rounded-lg border text-sm" value={idnForm.slug||""} onChange={e=>setIdnForm({...idnForm, slug:e.target.value})} /></div>
              <div><label className="text-xs text-stone-600">Logo URL</label><input className="w-full px-3 py-2 rounded-lg border text-sm" value={idnForm.logo_url||""} onChange={e=>setIdnForm({...idnForm, logo_url:e.target.value})} /></div>
              <div><label className="text-xs text-stone-600">Teléfono</label><input className="w-full px-3 py-2 rounded-lg border text-sm" value={idnForm.telefono||""} onChange={e=>setIdnForm({...idnForm, telefono:e.target.value})} /></div>
              <div><label className="text-xs text-stone-600">Correo</label><input className="w-full px-3 py-2 rounded-lg border text-sm" value={idnForm.correo||""} onChange={e=>setIdnForm({...idnForm, correo:e.target.value})} /></div>
              <div className="sm:col-span-2"><label className="text-xs text-stone-600">Dirección</label><input className="w-full px-3 py-2 rounded-lg border text-sm" value={idnForm.direccion||""} onChange={e=>setIdnForm({...idnForm, direccion:e.target.value})} /></div>
              <div><label className="text-xs text-stone-600">Color primario</label><input type="color" className="w-full h-10 rounded-lg border" value={idnForm.color_primario||"#D65F79"} onChange={e=>setIdnForm({...idnForm, color_primario:e.target.value})} /></div>
              <div><label className="text-xs text-stone-600">Color secundario</label><input type="color" className="w-full h-10 rounded-lg border" value={idnForm.color_secundario||"#B84A63"} onChange={e=>setIdnForm({...idnForm, color_secundario:e.target.value})} /></div>
              <div className="sm:col-span-2"><label className="text-xs text-stone-600">Footer / pie de página</label><textarea className="w-full px-3 py-2 rounded-lg border text-sm" rows="2" value={idnForm.footer_texto||""} onChange={e=>setIdnForm({...idnForm, footer_texto:e.target.value})} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!idnForm.activo} onChange={e=>setIdnForm({...idnForm, activo:e.target.checked})} /> Activa</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!idnForm.es_default} onChange={e=>setIdnForm({...idnForm, es_default:e.target.checked})} /> Identidad default</label>
            </div>
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={()=>setIdnForm(null)} className="px-4 py-2 rounded-lg border text-sm" data-testid="btn-idn-cancelar">Cancelar</button>
              <button onClick={saveIdentidad} className="px-4 py-2 rounded-lg bg-rose-700 text-white text-sm" data-testid="btn-idn-guardar">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Reglas comerciales */}
      <Card className="border-stone-200">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Calculator className="w-5 h-5 text-emerald-700" />
            <h2 className="font-display text-lg text-stone-900">Reglas comerciales</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Minimo operativo (personas)</Label>
              <Input type="number" min={1} value={cfg.minimo_personas} onChange={(e) => set("minimo_personas", parseInt(e.target.value) || 1)} disabled={readonly} className="border-stone-300 h-11" data-testid="input-minimo" />
              <p className="text-[10px] text-stone-500 mt-1">Si llegan menos personas, se cobra al minimo.</p>
            </div>
            <div>
              <Label className="text-xs">Anticipo para apartar fecha (MXN)</Label>
              <Input type="number" min={0} value={cfg.anticipo_default} onChange={(e) => set("anticipo_default", parseFloat(e.target.value) || 0)} disabled={readonly} className="border-stone-300 h-11" data-testid="input-anticipo" />
            </div>
            <div>
              <Label className="text-xs">Duracion base del evento (horas)</Label>
              <Input type="number" min={1} value={cfg.duracion_base_horas} onChange={(e) => set("duracion_base_horas", parseInt(e.target.value) || 1)} disabled={readonly} className="border-stone-300 h-11" data-testid="input-duracion" />
            </div>
            <div>
              <Label className="text-xs">Dias para liquidar saldo (antes del evento)</Label>
              <Input type="number" min={0} value={cfg.dias_liquidacion} onChange={(e) => set("dias_liquidacion", parseInt(e.target.value) || 0)} disabled={readonly} className="border-stone-300 h-11" data-testid="input-dias-liquidacion" />
            </div>
            <div>
              <Label className="text-xs">Vigencia de cotizacion (dias)</Label>
              <Input type="number" min={1} value={cfg.vigencia_dias} onChange={(e) => set("vigencia_dias", parseInt(e.target.value) || 1)} disabled={readonly} className="border-stone-300 h-11" data-testid="input-vigencia" />
            </div>
            <div>
              <Label className="text-xs">Tipo de cotizacion por defecto</Label>
              <Select
                value={cfg.tipo_cotizacion_default || "instalaciones_mas_menu"}
                onValueChange={(v) => {
                  if (v === "__nuevo__") { setNuevoTipoOpen(true); return; }
                  set("tipo_cotizacion_default", v);
                }}
                disabled={readonly}
              >
                <SelectTrigger className="h-11" data-testid="select-tipo-cot"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="instalaciones_mas_menu">Uso de instalaciones + menu</SelectItem>
                  <SelectItem value="paquete_completo">Paquete completo (instalaciones incluidas)</SelectItem>
                  {tiposCustom.map(t => (
                    <SelectItem key={t.key} value={t.key} data-testid={`tipo-custom-${t.key}`}>{t.label}</SelectItem>
                  ))}
                  {!readonly && (
                    <SelectItem value="__nuevo__" data-testid="tipo-custom-nuevo">+ Agregar tipo personalizado</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {nuevoTipoOpen && !readonly && (
                <div className="mt-2 flex flex-col sm:flex-row gap-2" data-testid="tipo-custom-input-wrap">
                  <Input
                    autoFocus
                    value={nuevoTipoNombre}
                    onChange={(e) => setNuevoTipoNombre(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarTipoCustom(); } if (e.key === "Escape") { setNuevoTipoOpen(false); setNuevoTipoNombre(""); } }}
                    placeholder='Ej. "Solo renta", "Boda Petit", "Catering a domicilio"'
                    className="border-stone-300 h-10 flex-1"
                    data-testid="input-tipo-custom-nombre"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={agregarTipoCustom} className="h-10" data-testid="btn-agregar-tipo-custom">Agregar</Button>
                    <Button size="sm" variant="outline" onClick={() => { setNuevoTipoOpen(false); setNuevoTipoNombre(""); }} className="h-10">Cancelar</Button>
                  </div>
                </div>
              )}
              {!readonly && tiposCustom.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="tipos-custom-lista">
                  {tiposCustom.map(t => (
                    <span key={t.key} className="inline-flex items-center gap-1 text-[11px] bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full border border-stone-200">
                      {t.label}
                      <button onClick={() => eliminarTipoCustom(t.key)} className="text-stone-500 hover:text-red-600" title="Eliminar tipo" data-testid={`btn-eliminar-tipo-${t.key}`}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-stone-500 mt-1">Actual: <strong>{tipoCotLabel}</strong></p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* IVA */}
      <Card className="border-stone-200">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-stone-700" />
            <h2 className="font-display text-lg text-stone-900">IVA</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded border border-stone-200">
              <div className="min-w-0">
                <p className="font-medium text-stone-900 text-sm">Cobrar IVA en cotizaciones</p>
                <p className="text-[10px] text-stone-500">Si esta activo, se suma al total automaticamente.</p>
              </div>
              <Switch checked={cfg.iva_activo} onCheckedChange={(v) => set("iva_activo", !!v)} disabled={readonly} data-testid="switch-iva" />
            </div>
            {cfg.iva_activo && (
              <div>
                <Label className="text-xs">Porcentaje de IVA</Label>
                <Input type="number" min={0} max={100} step={0.1} value={cfg.iva_porcentaje} onChange={(e) => set("iva_porcentaje", parseFloat(e.target.value) || 0)} disabled={readonly} className="border-stone-300 h-11" data-testid="input-iva-pct" />
              </div>
            )}
          </div>
          {!cfg.iva_activo && <p className="text-xs text-stone-500 mt-2 italic">El IVA esta desactivado. No se aplicara a las cotizaciones.</p>}
        </CardContent>
      </Card>

      {/* Mensajes y condiciones */}
      <Card className="border-stone-200">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="w-5 h-5 text-stone-700" />
            <h2 className="font-display text-lg text-stone-900">Mensajes y condiciones</h2>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Condiciones comerciales (visibles en la cotizacion del cliente)</Label>
              <Textarea rows={3} value={cfg.condiciones_comerciales} onChange={(e) => set("condiciones_comerciales", e.target.value)} disabled={readonly} className="border-stone-300" data-testid="textarea-condiciones" />
            </div>
            <div>
              <Label className="text-xs">Politicas de pago</Label>
              <Textarea rows={3} value={cfg.politicas_pago} onChange={(e) => set("politicas_pago", e.target.value)} disabled={readonly} className="border-stone-300" data-testid="textarea-politicas" />
            </div>
            <div>
              <Label className="text-xs">Plantilla de WhatsApp (opcional, vacio usa default)</Label>
              <Textarea rows={5} value={cfg.mensaje_whatsapp_template || ""} onChange={(e) => set("mensaje_whatsapp_template", e.target.value)} disabled={readonly} placeholder="Variables: {nombre_cliente} {fecha_evento} {tipo_evento} {adultos} {ninos} {total} {anticipo} {saldo}" className="border-stone-300 font-mono text-xs" data-testid="textarea-whatsapp" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vista publica y liga */}
      <Card className="border-emerald-200 bg-emerald-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-emerald-700" />
            <h2 className="font-display text-lg text-emerald-900">Liga del cotizador publico</h2>
          </div>
          <p className="text-xs text-emerald-800 mb-2">Compartela por WhatsApp, Instagram o como QR para que tus clientes coticen solos.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={publicUrl} readOnly className="flex-1 bg-white border-emerald-300 text-xs h-11 font-mono" data-testid="input-public-url" />
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Liga copiada al portapapeles"); }} className="border-emerald-300 h-11" data-testid="btn-copiar-liga">
              <Copy className="w-4 h-4 mr-2" /> Copiar liga
            </Button>
            <Button onClick={() => window.open(publicUrl, "_blank")} className="bg-emerald-900 hover:bg-emerald-800 h-11" data-testid="btn-abrir-cotizador">
              <ExternalLink className="w-4 h-4 mr-2" /> Abrir cotizador
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Catalogos editables - tarjetas con accesos directos */}
      <div>
        <h2 className="font-display text-xl text-stone-900 mb-3">Catalogos editables del cotizador</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <CatCard
            icon={ChefHat}
            color="emerald"
            title="Menus y paquetes"
            desc="Edita menus, precios adulto y nino, costo interno y visibilidad. Tambien aqui marcas un menu como 'paquete completo' para que NO se sume la renta aparte."
            actionLabel="Administrar menus"
            onClick={() => navigate("/panel/catalogo")}
            testId="card-cat-menus"
          />
          <CatCard
            icon={Zap}
            color="amber"
            title="Extras y servicios rapidos"
            desc="Edita DJ 5 horas, hora extra DJ, hora extra salon, decoracion, mesa de dulces, karaoke y todos los servicios opcionales."
            actionLabel="Administrar extras"
            onClick={() => navigate("/panel/servicios-rapidos")}
            testId="card-cat-extras"
          />
          <CatCard
            icon={Building2}
            color="blue"
            title="Uso de instalaciones"
            desc="Define el precio del uso de instalaciones por evento (cuando NO va incluido en un paquete completo)."
            actionLabel="Administrar instalaciones"
            onClick={() => navigate("/panel/servicios-rapidos")}
            testId="card-cat-instalaciones"
          />
        </div>
      </div>

      {/* Restore confirmation */}
      <AlertDialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar valores iniciales?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion regresara la configuracion del cotizador a sus valores por defecto (minimo 30, anticipo $5,000, vigencia 15 dias, IVA off, etc.).
              <br /><br />
              <strong>NO modificara cotizaciones, contratos ni eventos anteriores.</strong> Solo afecta a cotizaciones nuevas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={restore} className="bg-amber-700 hover:bg-amber-800" data-testid="btn-confirmar-restore">Si, restaurar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Barra de acciones fija inferior cuando se edita */}
      {editMode && (
        <div className="fixed bottom-0 left-0 right-0 md:left-72 bg-white border-t border-stone-200 px-4 py-3 flex justify-end gap-2 shadow-lg z-40">
          <Button variant="outline" onClick={cancel} className="h-11 border-stone-300" data-testid="btn-cancelar-bar">Cancelar</Button>
          <Button onClick={save} disabled={saving} className="h-11 bg-emerald-900 hover:bg-emerald-800 px-6" data-testid="btn-guardar-bar">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      )}
    </div>
  );
};

const CatCard = ({ icon: Icon, color, title, desc, actionLabel, onClick, testId }) => {
  const styles = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
  }[color] || "bg-stone-50 border-stone-200 text-stone-800";
  return (
    <Card className={`border ${styles}`} data-testid={testId}>
      <CardContent className="p-4 flex flex-col h-full">
        <Icon className="w-7 h-7 mb-2" />
        <h3 className="font-display text-lg mb-1">{title}</h3>
        <p className="text-xs flex-1 opacity-80">{desc}</p>
        <Button onClick={onClick} variant="outline" className="mt-3 bg-white h-10 border-stone-300 text-stone-800 hover:bg-stone-50">
          {actionLabel} <ArrowRight className="w-3 h-3 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default CotizadorConfig;
